import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { FileText, Wand2, Play, X, Plus } from 'lucide-react'
import type { GameFile, GameMode } from '@creanizer/types'

interface SessionCreateDialogProps {
  gameFiles: GameFile[]
  templates: Array<{ id: string; name: string; systemPrompt: string }>
  onStartWithFile: (sessionName: string, gameFile: GameFile) => void
  onStartWithTemplate: (sessionName: string, template: any) => void
  onStartFreeMode: (sessionName: string, systemPrompt: string) => void
  onCreateNewFile: () => void
  onClose: () => void
}

export function SessionCreateDialog({
  gameFiles,
  templates,
  onStartWithFile,
  onStartWithTemplate,
  onStartFreeMode,
  onCreateNewFile,
  onClose
}: SessionCreateDialogProps) {
  const [sessionName, setSessionName] = useState('')
  const [selectedTab, setSelectedTab] = useState<'structured' | 'free'>('structured')
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [customSystemPrompt, setCustomSystemPrompt] = useState('')
  
  const structuredFiles = gameFiles.filter(f => f.mode === 'structured')
  
  const handleStart = () => {
    if (!sessionName.trim()) {
      alert('セッション名を入力してください')
      return
    }
    
    if (selectedTab === 'structured') {
      const selectedFile = structuredFiles.find(f => f.id === selectedFileId)
      if (!selectedFile) {
        alert('ゲームファイルを選択してください')
        return
      }
      onStartWithFile(sessionName, selectedFile)
    } else {
      if (selectedTemplateId) {
        const template = templates.find(t => t.id === selectedTemplateId)
        if (template) {
          onStartWithTemplate(sessionName, template)
        }
      } else if (customSystemPrompt.trim()) {
        onStartFreeMode(sessionName, customSystemPrompt)
      } else {
        alert('テンプレートを選択するか、システムプロンプトを入力してください')
        return
      }
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">新しいゲームセッション</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* セッション名 */}
            <div>
              <Label htmlFor="session-name">セッション名 *</Label>
              <Input
                id="session-name"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                placeholder="例: ダンジョン探索 #1"
                autoFocus
              />
            </div>
            
            {/* モード選択 */}
            <Tabs value={selectedTab} onValueChange={(v) => setSelectedTab(v as 'structured' | 'free')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="structured">構造化モード</TabsTrigger>
                <TabsTrigger value="free">フリーモード</TabsTrigger>
              </TabsList>
              
              <TabsContent value="structured" className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>ゲームファイルを選択</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onCreateNewFile}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      新規作成
                    </Button>
                  </div>
                  
                  {structuredFiles.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center">
                        <p className="text-gray-600 mb-4">
                          利用可能なゲームファイルがありません
                        </p>
                        <Button onClick={onCreateNewFile}>
                          <Plus className="w-4 h-4 mr-2" />
                          ゲームファイルを作成
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-2 max-h-60 overflow-y-auto">
                      {structuredFiles.map((file) => (
                        <Card
                          key={file.id}
                          className={`cursor-pointer transition-colors ${
                            selectedFileId === file.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedFileId(file.id)}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium">{file.name}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {file.description || 'No description'}
                                </div>
                                <div className="text-xs text-gray-500 mt-2">
                                  パラメータ: {file.structuredMode?.parameters?.length || 0} |
                                  アクション: {file.structuredMode?.actions?.length || 0}
                                </div>
                              </div>
                              {selectedFileId === file.id && (
                                <div className="text-blue-600">
                                  ✓
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="free" className="space-y-4">
                <div>
                  <Label>テンプレートを選択（オプション）</Label>
                  {templates.length > 0 && (
                    <div className="grid gap-2 mb-4 max-h-40 overflow-y-auto">
                      {templates.map((template) => (
                        <Card
                          key={template.id}
                          className={`cursor-pointer transition-colors ${
                            selectedTemplateId === template.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSelectedTemplateId(template.id)
                            setCustomSystemPrompt('')
                          }}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between">
                              <div className="font-medium">{template.name}</div>
                              {selectedTemplateId === template.id && (
                                <div className="text-blue-600">✓</div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">または</span>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label htmlFor="custom-prompt">
                      カスタムシステムプロンプト
                    </Label>
                    <Textarea
                      id="custom-prompt"
                      value={customSystemPrompt}
                      onChange={(e) => {
                        setCustomSystemPrompt(e.target.value)
                        setSelectedTemplateId(null)
                      }}
                      placeholder="ゲームマスターとしての振る舞いを記述..."
                      rows={6}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        
        <div className="p-6 border-t">
          <div className="flex gap-2">
            <Button
              onClick={handleStart}
              disabled={!sessionName.trim() || (
                selectedTab === 'structured' 
                  ? !selectedFileId
                  : !selectedTemplateId && !customSystemPrompt.trim()
              )}
              className="flex-1"
            >
              <Play className="w-4 h-4 mr-2" />
              ゲームを開始
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              キャンセル
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}